import { useEffect, useContext, useMemo, useCallback } from 'react';

import { useAppSelector, useAppDispatch } from 'hooks';

import { ApplicationFormContext } from 'contexts/ApplicationFormContext';

import getValidationMessage from 'helpers/getValidationMessage';

import { getMandatoryFileValidations } from 'views/shared/ReferToUnderwriting/helpers';

import {
  addMissingMandatoryField,
  removeMissingMandatoryField,
  addRemark,
  removeRemark,
  updateRemark,
  addFile,
  removeFile,
} from 'reducers/autoPersonalFormReducer';

import {
  getFieldId,
  formatDateToString,
  buildMarketApprovalContext,
  isApprovalMarketMatching,
  isApprovalWithinBindingAuthority,
  isEmailOnlyMarket,
} from '../resolverUtils';
import type {
  UseMarketApprovalResolverConfig,
  MarketApprovalFieldIds,
  MarketApprovalResolverState,
  MarketApprovalResolverHandlers,
} from '../types';

export const useMarketApprovalResolver = (
  config: UseMarketApprovalResolverConfig,
) => {
  const {
    validationId,
    section,
    itemKey,
    formStateKey,
    showLOEOption,
    marketApprovalFileType,
    loeFileType,
    loeFileLabel,
    forceShow,
    customAlertMessage,
    remarksRequired = true,
    marketRequired = false,
    resolutionOnly = false,
    emailOnly: emailOnlyConfig = false,
    allowPhoneApproval = false,
    approvalNumberRequired = false,
  } = config;

  const { validations } = useContext(ApplicationFormContext);
  const dispatch = useAppDispatch();

  const mandatoryFileValidations = getMandatoryFileValidations(formStateKey);

  const alertMessage =
    customAlertMessage ?? getValidationMessage(validations, validationId);
  const show = forceShow === true ? true : !!alertMessage;

  const remark = useAppSelector((state) => {
    return state[formStateKey].remarks.find(
      (r: any) => r.validation_key === validationId && r.item_key === itemKey,
    );
  });

  const file = useAppSelector((state) => {
    return state[formStateKey].files.find(
      (f: any) => f.validation_key === validationId && f.item_key === itemKey,
    );
  });

  const customData =
    remark?.custom_data?.type === 'market_approval' ? remark.custom_data : null;
  const resolutionType = customData?.resolution_type || null;
  const approvalMethod = customData?.approval_method || null;
  const approvalDate = customData?.approval_date || null;
  const approvalTime = customData?.approval_time || null;
  const underwriterName = customData?.underwriter_name || null;
  const market = customData?.market || null;
  const approvalNumber = customData?.approval_number || null;
  const summaryOfMarketConversationText =
    customData?.summary_of_market_conversation_text ?? null;

  const quotedMarket = useAppSelector((state) => {
    const data = state[formStateKey].data;
    if (!data || !('company_and_broker_information' in data)) return null;
    return data.company_and_broker_information?.insurance_company || null;
  });

  const effectiveDate = useAppSelector((state) => {
    const data = state[formStateKey].data;
    if (!data || !('policy_period' in data)) return null;
    return data.policy_period?.effective_date || null;
  });

  const emailOnly =
    emailOnlyConfig || (!allowPhoneApproval && isEmailOnlyMarket(quotedMarket));

  const marketMismatch = !isApprovalMarketMatching(market, quotedMarket);
  const approvalOutsideBindingAuthority = !isApprovalWithinBindingAuthority(
    approvalDate,
    effectiveDate,
    quotedMarket,
  );

  const approvalContext = buildMarketApprovalContext(
    quotedMarket,
    effectiveDate,
  );

  // Determine current file type based on resolution path
  const currentFileType = useMemo(() => {
    if (showLOEOption && resolutionType === 'loe' && loeFileType) {
      return loeFileType;
    }
    if (
      resolutionType === 'market_approval' ||
      (!showLOEOption && approvalMethod === 'email')
    ) {
      return marketApprovalFileType;
    }
    return null;
  }, [
    showLOEOption,
    resolutionType,
    approvalMethod,
    loeFileType,
    marketApprovalFileType,
  ]);

  // Determine if file upload should be shown
  const showFileUpload = useMemo(() => {
    if (showLOEOption) {
      if (resolutionType === 'loe') return true;
      if (resolutionType === 'market_approval' && approvalMethod === 'email')
        return true;
    } else {
      if (approvalMethod === 'email') return true;
    }
    return false;
  }, [showLOEOption, resolutionType, approvalMethod]);

  // Check if an approval method (email or phone_call) is selected
  const hasApprovalMethod =
    approvalMethod === 'phone_call' || approvalMethod === 'email';

  // Determine if approval detail fields should be shown
  const showApprovalFields = useMemo(() => {
    if (showLOEOption) {
      return resolutionType === 'market_approval' && hasApprovalMethod;
    }
    return hasApprovalMethod;
  }, [showLOEOption, resolutionType, hasApprovalMethod]);

  // Determine if remarks text field should be shown/validated.
  const showRemarksField = useMemo(() => {
    if (showLOEOption && resolutionType === 'loe') return true;
    return showApprovalFields;
  }, [showLOEOption, resolutionType, showApprovalFields]);

  // Alias for backwards compatibility
  const showPhoneCallFields = showApprovalFields;

  // Generate field IDs for mandatory field tracking and navigation
  const fieldIds: MarketApprovalFieldIds = useMemo(
    () => ({
      resolution: getFieldId('Market Approval Resolution', itemKey),
      approvalMethod: getFieldId('Market Approval Method', itemKey),
      remarks: getFieldId('Market Approval Remarks', itemKey),
      date: getFieldId('Market Approval Date', itemKey),
      time: getFieldId('Market Approval Time', itemKey),
      underwriter: getFieldId('Market Approval Underwriter', itemKey),
      market: getFieldId('Market Approval Market', itemKey),
      approvalNumber: getFieldId('Market Approval Approval Number', itemKey),
    }),
    [itemKey],
  );

  // Initialize remark when validation is shown
  useEffect(() => {
    if (show) {
      dispatch(
        addRemark({
          validation_key: validationId,
          item_key: itemKey,
          text: '',
          status: 'created',
          text_validator_log_id: null,
          ai_result: '',
          section,
          custom_data: {
            type: 'market_approval',
            resolution_type: null,
            approval_method: emailOnly ? 'email' : null,
            approval_date: null,
            approval_time: null,
            underwriter_name: null,
            market: null,
            approval_number: null,
            summary_of_market_conversation_text: null,
          },
        }),
      );
    } else {
      dispatch(
        removeRemark({
          validation_key: validationId,
          item_key: itemKey,
        }),
      );
    }
  }, [show, validationId, itemKey, section, emailOnly, dispatch]);

  // Force email approval method when emailOnly is enabled
  useEffect(() => {
    if (!show || !emailOnly || !remark) return;
    if (approvalMethod === 'email') return;

    dispatch(
      updateRemark({
        validation_key: validationId,
        item_key: itemKey,
        data: {
          custom_data: {
            ...(customData || { type: 'market_approval' }),
            type: 'market_approval',
            approval_method: 'email',
          },
        },
      }),
    );
  }, [
    show,
    emailOnly,
    remark,
    approvalMethod,
    customData,
    validationId,
    itemKey,
    dispatch,
  ]);

  // Manage file upload based on current state
  useEffect(() => {
    if (show && showFileUpload && currentFileType) {
      dispatch(
        addFile({
          validation_key: validationId,
          item_key: itemKey,
          status: 'created',
          file_validator_log_id: null,
          ai_result: '',
          section,
          type: currentFileType,
          files: [],
          text:
            showLOEOption && resolutionType === 'loe'
              ? loeFileLabel || 'Letter of Experience'
              : 'Market Approval Email',
          mandatory:
            mandatoryFileValidations.includes(validationId) ||
            (showLOEOption && resolutionType === 'loe'),
        }),
      );
    } else {
      dispatch(
        removeFile({
          validation_key: validationId,
          item_key: itemKey,
        }),
      );
    }
  }, [
    show,
    showFileUpload,
    currentFileType,
    validationId,
    itemKey,
    section,
    showLOEOption,
    loeFileLabel,
    resolutionType,
    mandatoryFileValidations,
    dispatch,
  ]);

  useEffect(() => {
    if (!show || !remark) return;

    const requiredFieldsFilled =
      !!customData?.approval_date &&
      !!customData?.approval_time &&
      !!customData?.underwriter_name &&
      (!marketRequired || !!customData?.market?.trim()) &&
      (!approvalNumberRequired || !!customData?.approval_number?.trim());

    if (approvalMethod !== 'email') return;

    const isComplete =
      requiredFieldsFilled &&
      !!file &&
      (file.status === 'approved' || file.is_deferred);

    if (isComplete && remark.status !== 'approved') {
      dispatch(
        updateRemark({
          validation_key: validationId,
          item_key: itemKey,
          data: { status: 'approved' },
        }),
      );
    } else if (!isComplete && remark.status === 'approved') {
      dispatch(
        updateRemark({
          validation_key: validationId,
          item_key: itemKey,
          data: { status: 'created' },
        }),
      );
    }
  }, [
    show,
    remark,
    approvalMethod,
    customData,
    file,
    marketRequired,
    approvalNumberRequired,
    validationId,
    itemKey,
    dispatch,
  ]);

  // Manage mandatory field validation
  useEffect(() => {
    if (!show || !remark) {
      dispatch(removeMissingMandatoryField(fieldIds.resolution));
      dispatch(removeMissingMandatoryField(fieldIds.approvalMethod));
      dispatch(removeMissingMandatoryField(fieldIds.remarks));
      dispatch(removeMissingMandatoryField(fieldIds.date));
      dispatch(removeMissingMandatoryField(fieldIds.time));
      dispatch(removeMissingMandatoryField(fieldIds.underwriter));
      dispatch(removeMissingMandatoryField(fieldIds.market));
      dispatch(removeMissingMandatoryField(fieldIds.approvalNumber));
      return;
    }

    if (remark.custom_data?.type !== 'market_approval') {
      dispatch(removeMissingMandatoryField(fieldIds.resolution));
      dispatch(removeMissingMandatoryField(fieldIds.approvalMethod));
      dispatch(removeMissingMandatoryField(fieldIds.remarks));
      dispatch(removeMissingMandatoryField(fieldIds.date));
      dispatch(removeMissingMandatoryField(fieldIds.time));
      dispatch(removeMissingMandatoryField(fieldIds.underwriter));
      dispatch(removeMissingMandatoryField(fieldIds.market));
      dispatch(removeMissingMandatoryField(fieldIds.approvalNumber));
      return;
    }

    // Resolution type validation (only for LOE option mode)
    if (showLOEOption) {
      if (!resolutionType) {
        dispatch(
          addMissingMandatoryField({
            name: fieldIds.resolution,
            section,
          }),
        );
      } else {
        dispatch(removeMissingMandatoryField(fieldIds.resolution));
      }
    }

    if (resolutionOnly) {
      dispatch(removeMissingMandatoryField(fieldIds.approvalMethod));
      dispatch(removeMissingMandatoryField(fieldIds.remarks));
      dispatch(removeMissingMandatoryField(fieldIds.date));
      dispatch(removeMissingMandatoryField(fieldIds.time));
      dispatch(removeMissingMandatoryField(fieldIds.underwriter));
      dispatch(removeMissingMandatoryField(fieldIds.market));
      dispatch(removeMissingMandatoryField(fieldIds.approvalNumber));
      return;
    }

    // Approval method validation (for market approval path or non-LOE mode)
    const needsApprovalMethod =
      !emailOnly &&
      ((showLOEOption && resolutionType === 'market_approval') ||
        !showLOEOption);
    if (needsApprovalMethod && !approvalMethod) {
      dispatch(
        addMissingMandatoryField({
          name: fieldIds.approvalMethod,
          section,
        }),
      );
    } else {
      dispatch(removeMissingMandatoryField(fieldIds.approvalMethod));
    }

    // Remarks validation (skipped when remarksRequired is false)
    if (
      remarksRequired &&
      showRemarksField &&
      (!remark.text || remark.text.trim().length === 0)
    ) {
      dispatch(
        addMissingMandatoryField({
          name: fieldIds.remarks,
          section,
        }),
      );
    } else {
      dispatch(removeMissingMandatoryField(fieldIds.remarks));
    }

    // Phone call fields validation
    if (showPhoneCallFields) {
      if (!customData?.approval_date || approvalOutsideBindingAuthority) {
        dispatch(
          addMissingMandatoryField({
            name: fieldIds.date,
            section,
          }),
        );
      } else {
        dispatch(removeMissingMandatoryField(fieldIds.date));
      }

      if (!customData?.approval_time) {
        dispatch(
          addMissingMandatoryField({
            name: fieldIds.time,
            section,
          }),
        );
      } else {
        dispatch(removeMissingMandatoryField(fieldIds.time));
      }

      if (
        !customData?.underwriter_name ||
        customData.underwriter_name.trim().length === 0
      ) {
        dispatch(
          addMissingMandatoryField({
            name: fieldIds.underwriter,
            section,
          }),
        );
      } else {
        dispatch(removeMissingMandatoryField(fieldIds.underwriter));
      }

      if (
        marketMismatch ||
        (marketRequired &&
          (!customData?.market || customData.market.trim().length === 0))
      ) {
        dispatch(
          addMissingMandatoryField({
            name: fieldIds.market,
            section,
          }),
        );
      } else {
        dispatch(removeMissingMandatoryField(fieldIds.market));
      }

      if (
        approvalNumberRequired &&
        (!customData?.approval_number ||
          customData.approval_number.trim().length === 0)
      ) {
        dispatch(
          addMissingMandatoryField({
            name: fieldIds.approvalNumber,
            section,
          }),
        );
      } else {
        dispatch(removeMissingMandatoryField(fieldIds.approvalNumber));
      }
    } else {
      dispatch(removeMissingMandatoryField(fieldIds.date));
      dispatch(removeMissingMandatoryField(fieldIds.time));
      dispatch(removeMissingMandatoryField(fieldIds.underwriter));
      dispatch(removeMissingMandatoryField(fieldIds.market));
      dispatch(removeMissingMandatoryField(fieldIds.approvalNumber));
    }
  }, [
    show,
    remark,
    customData,
    resolutionType,
    approvalMethod,
    showLOEOption,
    showRemarksField,
    showPhoneCallFields,
    remarksRequired,
    marketRequired,
    approvalNumberRequired,
    resolutionOnly,
    marketMismatch,
    approvalOutsideBindingAuthority,
    emailOnly,
    section,
    fieldIds,
    dispatch,
  ]);

  // Handlers
  const handleResolutionTypeChange = useCallback(
    (value: string) => {
      dispatch(
        updateRemark({
          validation_key: validationId,
          item_key: itemKey,
          data: {
            text: '',
            status: 'created',
            custom_data: {
              type: 'market_approval',
              resolution_type: value as 'loe' | 'market_approval',
              approval_method: null,
              approval_date: null,
              approval_time: null,
              underwriter_name: null,
              market: null,
              approval_number: null,
              summary_of_market_conversation_text: null,
            },
          },
        }),
      );
    },
    [dispatch, validationId, itemKey],
  );

  const handleApprovalMethodChange = useCallback(
    (value: string) => {
      dispatch(
        updateRemark({
          validation_key: validationId,
          item_key: itemKey,
          data: {
            status: 'created',
            custom_data: {
              ...customData,
              type: 'market_approval',
              approval_method: value as 'email' | 'phone_call',
              approval_date: null,
              approval_time: null,
              underwriter_name: null,
              approval_number: null,
              summary_of_market_conversation_text: null,
            },
          },
        }),
      );
    },
    [dispatch, validationId, itemKey, customData],
  );

  const handleRemarksChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      dispatch(
        updateRemark({
          validation_key: validationId,
          item_key: itemKey,
          data: {
            text: e.target.value,
            status: 'created',
          },
        }),
      );
    },
    [dispatch, validationId, itemKey],
  );

  const handleDateChange = useCallback(
    (date: Date | null) => {
      dispatch(
        updateRemark({
          validation_key: validationId,
          item_key: itemKey,
          data: {
            custom_data: {
              ...customData,
              type: 'market_approval',
              approval_date: formatDateToString(date),
            },
          },
        }),
      );
    },
    [dispatch, validationId, itemKey, customData],
  );

  const handleTimeChange = useCallback(
    (time: string) => {
      dispatch(
        updateRemark({
          validation_key: validationId,
          item_key: itemKey,
          data: {
            custom_data: {
              ...customData,
              type: 'market_approval',
              approval_time: time || null,
            },
          },
        }),
      );
    },
    [dispatch, validationId, itemKey, customData],
  );

  const handleUnderwriterNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      dispatch(
        updateRemark({
          validation_key: validationId,
          item_key: itemKey,
          data: {
            custom_data: {
              ...customData,
              type: 'market_approval',
              underwriter_name: e.target.value,
            },
          },
        }),
      );
    },
    [dispatch, validationId, itemKey, customData],
  );

  const handleMarketChange = useCallback(
    (value: string) => {
      dispatch(
        updateRemark({
          validation_key: validationId,
          item_key: itemKey,
          data: {
            custom_data: {
              ...customData,
              type: 'market_approval',
              market: value || null,
            },
          },
        }),
      );
    },
    [dispatch, validationId, itemKey, customData],
  );

  const handleApprovalNumberChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      dispatch(
        updateRemark({
          validation_key: validationId,
          item_key: itemKey,
          data: {
            custom_data: {
              ...customData,
              type: 'market_approval',
              approval_number: e.target.value,
            },
          },
        }),
      );
    },
    [dispatch, validationId, itemKey, customData],
  );

  const handleSummaryChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      dispatch(
        updateRemark({
          validation_key: validationId,
          item_key: itemKey,
          data: {
            custom_data: {
              ...customData,
              type: 'market_approval',
              summary_of_market_conversation_text: e.target.value,
            },
          },
        }),
      );
    },
    [dispatch, validationId, itemKey, customData],
  );

  // Determine compact alert status based on completion state
  const getCompactAlertStatus = useCallback(() => {
    if (!remark) return 'warning';

    if (remark.custom_data?.type !== 'market_approval') {
      return remark.status === 'approved' ? 'success' : 'warning';
    }

    // Check if resolution path is complete
    if (showLOEOption && !resolutionType) return 'warning';
    if (
      !approvalMethod &&
      (resolutionType === 'market_approval' || !showLOEOption)
    ) {
      return 'warning';
    }

    // LOE path: check remark and file
    if (showLOEOption && resolutionType === 'loe') {
      if (remark.status !== 'approved') return 'warning';
      if (file && file.status !== 'approved') return 'warning';
      return 'success';
    }

    // Market approval path (email or phone_call): check required fields
    if (hasApprovalMethod) {
      if (approvalMethod === 'email' && file && file.status !== 'approved')
        return 'warning';
      if (remark.status !== 'approved') return 'warning';
      if (!customData?.approval_date || approvalOutsideBindingAuthority)
        return 'warning';
      if (!customData?.approval_time) return 'warning';
      if (!customData?.underwriter_name) return 'warning';
      if (marketMismatch) return 'warning';
      if (marketRequired && !customData?.market?.trim()) return 'warning';
      if (approvalNumberRequired && !customData?.approval_number?.trim())
        return 'warning';
      return 'success';
    }

    return 'warning';
  }, [
    remark,
    file,
    showLOEOption,
    resolutionType,
    approvalMethod,
    hasApprovalMethod,
    customData,
    marketRequired,
    approvalNumberRequired,
    marketMismatch,
    approvalOutsideBindingAuthority,
  ]);

  const state: MarketApprovalResolverState = {
    remark,
    file,
    resolutionType,
    approvalMethod,
    approvalDate,
    approvalTime,
    underwriterName,
    market,
    approvalNumber,
    summaryOfMarketConversationText,
    show,
    showFileUpload,
    showRemarksField,
    showPhoneCallFields,
    emailOnly,
    marketMismatch,
    approvalOutsideBindingAuthority,
    approvalContext,
  };

  const handlers: MarketApprovalResolverHandlers = {
    handleResolutionTypeChange,
    handleApprovalMethodChange,
    handleRemarksChange,
    handleDateChange,
    handleTimeChange,
    handleUnderwriterNameChange,
    handleMarketChange,
    handleApprovalNumberChange,
    handleSummaryChange,
  };

  return {
    state,
    handlers,
    fieldIds,
    alertMessage,
    getCompactAlertStatus,
  };
};
